import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Fragment, useEffect, useState } from 'react';
import { RulesEditor, rulesValid } from '../components/RulesEditor';
import { Topbar } from '../components/Shell';
import { Empty, ErrorBox, Field, Loading, Seg, useAction } from '../components/ui';
import { get, post, put, qs } from '../lib/api';
import { fmtAgo, fmtDateTime } from '../lib/format';
import type { AuditEntry, BoardRow, Competition, GameSettings, Round } from '../lib/types';
import type { AdPlacement, AdSenseSettings } from '@fp/core/settings';

// ================================================================ boards and standings

export function BoardsPage() {
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<{ settings: GameSettings }>('/admin/settings') });
  const season = settings.data?.settings.currentSeason ?? '';
  const rounds = useQuery({ queryKey: ['rounds', season], queryFn: () => get<{ rounds: Round[] }>(`/admin/rounds${qs({ season })}`), enabled: !!season });
  const comps = useQuery({ queryKey: ['competitions'], queryFn: () => get<{ competitions: Competition[] }>('/admin/competitions') });
  const [scope, setScope] = useState('');
  const eff = scope || (season ? `season:${season}` : '');
  const board = useQuery({ queryKey: ['board', eff], queryFn: () => get<{ rows: BoardRow[] }>(`/admin/boards/${eff}?limit=100`), enabled: !!eff });
  const drift = useQuery({ queryKey: ['drift'], queryFn: () => get<{ drift: number; needsRescore: number }>('/admin/standings/drift'), enabled: false });
  const { busy, run } = useAction();
  const qc = useQueryClient();
  return (
    <>
      <Topbar title="Boards and standings">
        <select className="select" aria-label="Board" value={eff} onChange={(e) => setScope(e.target.value)}>
          {season && <option value={`season:${season}`}>Season {season}</option>}
          {rounds.data?.rounds.map((r) => (
            <option key={r.id} value={`round:${r.id}`}>
              {r.name}
            </option>
          ))}
          {comps.data?.competitions.map((c) => (
            <option key={c.id} value={`competition:${c.id}`}>
              {c.name} {c.season}
            </option>
          ))}
        </select>
      </Topbar>
      <div className="page">
        <div className="cols cols-main">
          <section className="card">
            <div className="card-head">
              <h2>Top 100</h2>
              <span className="muted small">Verified, unbanned players. Ties share a rank.</span>
            </div>
            {board.isLoading ? (
              <Loading />
            ) : board.data?.rows.length === 0 ? (
              <Empty>No points on this board yet.</Empty>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Player</th>
                    <th className="right">Points</th>
                    <th className="right">Exact</th>
                    <th className="right">Results</th>
                    <th className="right">Predictions</th>
                  </tr>
                </thead>
                <tbody>
                  {board.data?.rows.map((r) => (
                    <tr key={r.userId}>
                      <td className="display num" style={{ fontSize: 17 }}>
                        {r.rank}
                      </td>
                      <td>
                        <Link to="/users/$userId" params={{ userId: r.userId }}>
                          {r.name}
                        </Link>
                      </td>
                      <td className="right display num" style={{ fontSize: 17 }}>
                        {r.points}
                      </td>
                      <td className="right num">{r.exactCount}</td>
                      <td className="right num">{r.outcomeCount}</td>
                      <td className="right num">{r.predictionsCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          <section className="card">
            <div className="card-head">
              <h2>Standings tools</h2>
            </div>
            <div className="card-body stack">
              <p className="muted small" style={{ margin: 0 }}>
                Running totals are updated with each re-score. The drift check recomputes them from scratch and counts rows that differ; it should be 0.
              </p>
              <div className="buttons">
                <button type="button" className="btn" disabled={drift.isFetching} onClick={() => drift.refetch()}>
                  Run drift check
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      const r = await post<{ rescored: number }>('/admin/standings/sweep');
                      await drift.refetch();
                      return r;
                    }, 'Pending re-scores done')
                  }
                >
                  Re-score pending matches
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await post('/admin/standings/rebuild');
                      await drift.refetch();
                      await qc.invalidateQueries({ queryKey: ['board'] });
                    }, 'Standings rebuilt')
                  }
                >
                  Rebuild all standings
                </button>
              </div>
              {drift.data && (
                <div className={drift.data.drift === 0 ? 'note-box' : 'err-box'} style={drift.data.drift === 0 ? { background: 'var(--green-soft)', color: 'var(--green-ink)' } : undefined}>
                  {drift.data.drift === 0 ? 'No drift: every running total matches a full recount.' : `${drift.data.drift} rows differ. Rebuild the standings.`} {drift.data.needsRescore} match(es) waiting to be re-scored.
                </div>
              )}
              {drift.error && <ErrorBox error={drift.error} />}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

// ================================================================ data issues

const REASON: Record<string, [string, string]> = {
  frozen: ['Feed update ignored: match frozen', 'An admin changed this match by hand, so feed updates are ignored until you hand it back to the feed.'],
  settled: ['Update refused for a confirmed match', 'The result was confirmed. Correct it by hand if the feed is right.'],
  stale_status: ['Out-of-order status from the feed', 'The feed sent a status that goes backwards. Usually harmless.'],
  unbacked_result_change: ['Score change without goal events', 'The feed changed a final score its goal list does not back up. Held back.'],
  unknown_player: ['Unknown scorer', "A goal's scorer isn't in our player list, so first-scorer points wait. Add the player to the squad."],
};

interface Issue {
  id: number;
  matchId: number | null;
  reason: string;
  note: string | null;
  detail: unknown;
  count: number;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  resolution: string | null;
  homeName: string | null;
  awayName: string | null;
  roundName: string | null;
}

export function IssuesPage() {
  const [open, setOpen] = useState<'open' | 'resolved'>('open');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['issues', open], queryFn: () => get<{ issues: Issue[] }>(`/admin/issues?open=${open === 'open'}`) });
  const { busy, run } = useAction();
  const [feed, setFeed] = useState('');
  const [result, setResult] = useState<string | null>(null);
  return (
    <>
      <Topbar title="Data issues" sub="Feed updates the game refused or couldn't fully apply">
        <Seg label="Show" value={open} onChange={setOpen} options={[['open', 'Open'], ['resolved', 'Resolved']]} />
      </Topbar>
      <div className="page">
        <div className="cols cols-main">
          <section className="card">
            {q.isLoading ? (
              <Loading />
            ) : q.data?.issues.length === 0 ? (
              <Empty>{open === 'open' ? 'Nothing to look at.' : 'No resolved issues yet.'}</Empty>
            ) : (
              q.data?.issues.map((i) => {
                const [title, help] = REASON[i.reason] ?? [i.reason, ''];
                return (
                  <div key={i.id} className="row" style={{ padding: '14px 16px', borderTop: '1px solid var(--line-2)', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                    <span className="dot" style={{ background: i.reason === 'unbacked_result_change' ? 'var(--red)' : 'var(--orange)', marginTop: 6 }} />
                    <div className="grow stack" style={{ gap: 4 }}>
                      <strong>{title}</strong>
                      <span className="small">
                        {i.matchId ? (
                          <Link to="/matches/$matchId" params={{ matchId: String(i.matchId) }}>
                            {i.homeName} v {i.awayName}
                          </Link>
                        ) : (
                          'Unknown match'
                        )}
                        {i.roundName && <span className="muted"> · {i.roundName}</span>}
                        {i.note && <span className="muted"> · {i.note}</span>}
                      </span>
                      <span className="muted small">
                        {help} Seen {i.count}×, last {fmtAgo(i.lastSeenAt)}.
                      </span>
                      {i.resolution && <span className="small">Resolved: {i.resolution}</span>}
                    </div>
                    {!i.resolvedAt && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            await post(`/admin/issues/${i.id}/resolve`, { resolution: 'Checked' });
                            await qc.invalidateQueries({ queryKey: ['issues'] });
                            await qc.invalidateQueries({ queryKey: ['dashboard'] });
                          }, 'Resolved')
                        }
                      >
                        Mark resolved
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </section>
          <section className="card">
            <div className="card-head">
              <h2>Replay a feed update</h2>
            </div>
            <div className="card-body stack">
              <p className="muted small" style={{ margin: 0 }}>
                Paste one provider-neutral <code>FeedUpdate</code> (what a provider adapter produces) to apply it as if it came from the poller. Useful for replaying a
                message or testing an adapter.
              </p>
              <textarea className="textarea" rows={10} value={feed} onChange={(e) => setFeed(e.target.value)} placeholder='{"provider":"api-football","externalId":"1001","status":"FIRST_HALF",...}' />
              <button
                type="button"
                className="btn"
                disabled={busy || !feed.trim()}
                onClick={() =>
                  run(async () => {
                    let body: unknown;
                    try {
                      body = JSON.parse(feed);
                    } catch {
                      throw new Error('That is not valid JSON');
                    }
                    const r = await post('/admin/feed/ingest', body);
                    setResult(JSON.stringify(r, null, 2));
                    await qc.invalidateQueries({ queryKey: ['issues'] });
                  })
                }
              >
                Apply update
              </button>
              {result && <pre className="json">{result}</pre>}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

// ================================================================ settings

export function SettingsPage() {
  const q = useQuery({ queryKey: ['settings'], queryFn: () => get<{ settings: GameSettings }>('/admin/settings') });
  const [s, setS] = useState<GameSettings | null>(null);
  useEffect(() => {
    if (q.data && !s) setS(q.data.settings);
  }, [q.data, s]);
  const { busy, run } = useAction();
  const qc = useQueryClient();
  if (q.isLoading || !s) return <Loading />;
  if (q.error) return <div className="page"><ErrorBox error={q.error} /></div>;
  const set = <K extends keyof GameSettings>(k: K, v: GameSettings[K]) => setS({ ...s, [k]: v });
  const dirty = JSON.stringify(s) !== JSON.stringify(q.data?.settings);
  return (
    <>
      <Topbar
        title="Settings"
        sub="Everything about the game you can change without a deploy"
        actions={
          <>
            <button type="button" className="btn" disabled={!dirty} onClick={() => setS(q.data!.settings)}>
              Discard changes
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !dirty || !rulesValid(s.defaultRules)}
              onClick={() =>
                run(async () => {
                  const r = await put<{ settings: GameSettings }>('/admin/settings', s);
                  qc.setQueryData(['settings'], r);
                  setS(r.settings);
                }, 'Settings saved')
              }
            >
              Save settings
            </button>
          </>
        }
      />
      <div className="page">
        <div className="cols cols-2">
          <section className="card">
            <div className="card-head">
              <h2>Game</h2>
            </div>
            <div className="card-body stack">
              <Field label="Game name" hint="Shown in the player site's header, titles and emails">
                <input className="input" value={s.gameName} maxLength={40} onChange={(e) => set('gameName', e.target.value)} />
              </Field>
              <Field label="Current season" hint="Season board and new rounds use this">
                <input className="input" value={s.currentSeason} onChange={(e) => set('currentSeason', e.target.value)} />
              </Field>
              <Field label="Announcement banner" hint="Shown across the top of the player site. Empty = none.">
                <input className="input" value={s.announcement} maxLength={200} onChange={(e) => set('announcement', e.target.value)} />
              </Field>
              <label className="check">
                <input type="checkbox" checked={s.registrationOpen} onChange={(e) => set('registrationOpen', e.target.checked)} /> Sign-ups open
              </label>
              <label className="check">
                <input type="checkbox" checked={s.maintenanceMode} onChange={(e) => set('maintenanceMode', e.target.checked)} /> Maintenance mode (players can look but not change anything)
              </label>
              <Field label="Maintenance message">
                <input className="input" value={s.maintenanceMessage} maxLength={280} onChange={(e) => set('maintenanceMessage', e.target.value)} />
              </Field>
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h2>Matchdays</h2>
            </div>
            <div className="card-body form-grid">
              <Field label="Matches per weekly slate" hint="Guidance shown on the slate page">
                <input className="input" type="number" min={1} max={50} value={s.slateTargetSize} onChange={(e) => set('slateTargetSize', Number(e.target.value))} />
              </Field>
              <Field label="Default lock" hint="Minutes before kickoff, for new competitions">
                <input className="input" type="number" min={0} max={1440} value={s.defaultLockMinutes} onChange={(e) => set('defaultLockMinutes', Number(e.target.value))} />
              </Field>
              <Field label="Reopen after a move of" hint="Hours. A locked match moved later than this reopens.">
                <input className="input" type="number" min={1} max={720} value={s.reopenIfMovedLaterByHours} onChange={(e) => set('reopenIfMovedLaterByHours', Number(e.target.value))} />
              </Field>
              <Field label="Confirm results after" hint="Hours after full time">
                <input className="input" type="number" min={0} max={720} value={s.settleAfterHours} onChange={(e) => set('settleAfterHours', Number(e.target.value))} />
              </Field>
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h2>Default points table</h2>
              <span className="muted small">For new competitions. Existing ones keep their own.</span>
            </div>
            <div className="card-body">
              <RulesEditor value={s.defaultRules} onChange={(r) => set('defaultRules', r)} />
            </div>
          </section>

          <div className="stack" style={{ gap: 20 }}>
            <section className="card">
              <div className="card-head">
                <h2>Ads</h2>
              </div>
              <div className="card-body stack" style={{ gap: 4 }}>
                <label className="check">
                  <input type="checkbox" checked={s.ads.enabled} onChange={(e) => set('ads', { ...s.ads, enabled: e.target.checked })} /> Show ads
                </label>
                {(['slateTop', 'resultsMid', 'liveBottom', 'matchPage', 'articles'] as const).map((k) => (
                  <label key={k} className="check" style={{ paddingLeft: 26, opacity: s.ads.enabled ? 1 : 0.5 }}>
                    <input type="checkbox" disabled={!s.ads.enabled} checked={s.ads[k]} onChange={(e) => set('ads', { ...s.ads, [k]: e.target.checked })} />
                    {
                      {
                        slateTop: 'Banner above the weekly slate',
                        resultsMid: 'Unit on the results page',
                        liveBottom: 'Banner below the live matchday',
                        matchPage: 'Banners on match pages (under the scoreboard and at the end)',
                        articles: 'Units in news articles (top, mid-article and end) and the news list',
                      }[k]
                    }
                  </label>
                ))}
                <AdSenseFields value={s.ads.adsense} onChange={(adsense) => set('ads', { ...s.ads, adsense })} />
              </div>
            </section>
            <section className="card">
              <div className="card-head">
                <h2>Articles</h2>
              </div>
              <div className="card-body stack" style={{ gap: 4 }}>
                <label className="check">
                  <input type="checkbox" checked={s.articles.autoPreview} onChange={(e) => set('articles', { ...s.articles, autoPreview: e.target.checked })} /> The AI
                  writes a match's preview when the match is published
                </label>
                <label className="check">
                  <input type="checkbox" checked={s.articles.autoReport} onChange={(e) => set('articles', { ...s.articles, autoReport: e.target.checked })} /> The AI
                  writes its report
                  <input
                    className="input"
                    type="number"
                    min={0}
                    max={1440}
                    aria-label="Minutes after full time"
                    value={s.articles.reportDelayMinutes}
                    onChange={(e) => set('articles', { ...s.articles, reportDelayMinutes: Number(e.target.value) })}
                    style={{ width: 70, margin: '0 6px' }}
                  />
                  minutes after full time
                </label>
                <label className="check">
                  <input type="checkbox" checked={s.articles.autoPublish} onChange={(e) => set('articles', { ...s.articles, autoPublish: e.target.checked })} />
                  Publish what it writes on its own. An article that contradicts its match data never goes out: it waits as a draft. Players only see a match's articles
                  once its week opens.
                </label>
              </div>
            </section>
            <section className="card">
              <div className="card-head">
                <h2>Data feed</h2>
              </div>
              <div className="card-body stack" style={{ gap: 10 }}>
                <span className="muted small">
                  Every call counts against the football data provider's quota. Live scores are fetched only from 5 minutes before kickoff to full time.
                </span>
                <Field label="Start checking a week" hint="Hours before it starts: kickoff moves and postponements of later weeks aren't fetched">
                  <input className="input" type="number" min={1} max={168} value={s.feed.weekLeadHours} onChange={(e) => set('feed', { ...s.feed, weekLeadHours: Number(e.target.value) })} />
                </Field>
                <Field label="Schedule check every" hint="Minutes, for the weeks being checked">
                  <input className="input" type="number" min={15} max={720} value={s.feed.scheduleCheckMinutes} onChange={(e) => set('feed', { ...s.feed, scheduleCheckMinutes: Number(e.target.value) })} />
                </Field>
                <Field label="Live match details at least every" hint="Minutes; sooner whenever the score or status moves">
                  <input className="input" type="number" min={1} max={30} value={s.feed.liveDetailMinutes} onChange={(e) => set('feed', { ...s.feed, liveDetailMinutes: Number(e.target.value) })} />
                </Field>
              </div>
            </section>
            <section className="card">
              <div className="card-head">
                <h2>Search engines and AI</h2>
              </div>
              <div className="card-body stack" style={{ gap: 8 }}>
                <label className="check">
                  <input type="checkbox" checked={s.seo.indexing} onChange={(e) => set('seo', { ...s.seo, indexing: e.target.checked })} /> Search engines may
                  index the player site (robots.txt, sitemap.xml)
                </label>
                <label className="check" style={{ paddingLeft: 26, opacity: s.seo.indexing ? 1 : 0.5 }}>
                  <input type="checkbox" disabled={!s.seo.indexing} checked={s.seo.aiCrawlers} onChange={(e) => set('seo', { ...s.seo, aiCrawlers: e.target.checked })} />
                  AI assistants and answer engines may read it too (GPTBot, ClaudeBot, PerplexityBot, Google-Extended...), with /llms.txt
                </label>
                <Field label="Site description" hint={`Search results, social cards and llms.txt · ${s.seo.description.length}/300`}>
                  <textarea className="textarea" rows={3} maxLength={300} value={s.seo.description} onChange={(e) => set('seo', { ...s.seo, description: e.target.value })} />
                </Field>
              </div>
            </section>
            <section className="card">
              <div className="card-head">
                <h2>Private leagues</h2>
              </div>
              <div className="card-body form-grid">
                <label className="check" style={{ gridColumn: '1 / -1' }}>
                  <input type="checkbox" checked={s.leagues.allowCreate} onChange={(e) => set('leagues', { ...s.leagues, allowCreate: e.target.checked })} /> Players can create leagues
                </label>
                <Field label="Members per league">
                  <input className="input" type="number" min={2} max={5000} value={s.leagues.maxMembers} onChange={(e) => set('leagues', { ...s.leagues, maxMembers: Number(e.target.value) })} />
                </Field>
                <Field label="Leagues one player can own">
                  <input className="input" type="number" min={0} max={100} value={s.leagues.maxOwnedPerUser} onChange={(e) => set('leagues', { ...s.leagues, maxOwnedPerUser: Number(e.target.value) })} />
                </Field>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

// ================================================================ audit log

export function AuditPage() {
  const [entity, setEntity] = useState('');
  const [pages, setPages] = useState<AuditEntry[][]>([]);
  const [before, setBefore] = useState<number | undefined>(undefined);
  const q = useQuery({
    queryKey: ['audit', entity, before],
    queryFn: () => get<{ entries: AuditEntry[] }>(`/admin/audit${qs({ entity, before, limit: 100 })}`),
  });
  useEffect(() => {
    if (!q.data) return;
    setPages((p) => (before === undefined ? [q.data.entries] : [...p, q.data.entries]));
  }, [q.data, before]);
  const rows = pages.flat();
  const [open, setOpen] = useState<number | null>(null);
  return (
    <>
      <Topbar title="Audit log" sub="Every admin action and every forced change, newest first">
        <select
          className="select"
          aria-label="Entity"
          value={entity}
          onChange={(e) => {
            setEntity(e.target.value);
            setBefore(undefined);
            setPages([]);
          }}
        >
          <option value="">Everything</option>
          {['match', 'team', 'competition', 'player', 'round', 'user', 'league', 'settings', 'feed_issue', 'standings'].map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
      </Topbar>
      <div className="page">
        <div className="card">
          {rows.length === 0 && q.isLoading && <Loading />}
          {rows.length === 0 && !q.isLoading && <Empty>No entries.</Empty>}
          {rows.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>On</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Fragment key={r.id}>
                    <tr>
                      <td className="num">{fmtDateTime(r.at)}</td>
                      <td>{r.actorName ?? (r.actorUserId ? r.actorUserId : 'system')}</td>
                      <td>
                        <strong>{r.action}</strong>
                      </td>
                      <td>
                        {r.entity === 'match' ? (
                          <Link to="/matches/$matchId" params={{ matchId: r.entityId }}>
                            match #{r.entityId}
                          </Link>
                        ) : (
                          `${r.entity} #${r.entityId}`
                        )}
                      </td>
                      <td className="right">
                        {(r.before !== null || r.after !== null) && (
                          <button type="button" className="btn btn-sm btn-ghost" aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? null : r.id)}>
                            {open === r.id ? 'Hide' : 'Details'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {open === r.id && (
                      <tr>
                        <td colSpan={5}>
                          <div className="cols cols-2" style={{ gap: 12 }}>
                            <div>
                              <div className="muted small">Before</div>
                              <pre className="json">{JSON.stringify(r.before, null, 2)}</pre>
                            </div>
                            <div>
                              <div className="muted small">After</div>
                              <pre className="json">{JSON.stringify(r.after, null, 2)}</pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
          {q.data && q.data.entries.length === 100 && (
            <div className="card-foot">
              <button type="button" className="btn btn-sm" onClick={() => setBefore(rows[rows.length - 1]?.id)}>
                Load older
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const PLACEMENT_LABEL: Record<AdPlacement, string> = {
  slateTop: 'Above the weekly slate',
  resultsMid: 'Results page',
  liveBottom: 'Below the live matchday',
  matchTop: 'Match page, under the scoreboard',
  matchBottom: 'Match page, at the end',
  newsList: 'News list',
  articleTop: 'Article, top',
  articleMid: 'Article, mid-article',
  articleBottom: 'Article, end',
};

/** Google AdSense: publisher ID, ad units, and who asks visitors for consent. */
function AdSenseFields({ value: a, onChange }: { value: AdSenseSettings; onChange: (a: AdSenseSettings) => void }) {
  const clientOk = !a.client || /^ca-pub-\d{10,20}$/.test(a.client);
  const unitOk = (v: string) => !v || /^\d{5,20}$/.test(v);
  return (
    <div className="stack" style={{ gap: 12, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
      <strong>Google AdSense</strong>
      <Field
        label="Publisher ID"
        hint="From AdSense → Account → Account information. Empty = placeholders instead of ads (development)."
        error={clientOk ? null : 'Looks like ca-pub-1234567890123456'}
      >
        <input className="input" value={a.client} placeholder="ca-pub-1234567890123456" onChange={(e) => onChange({ ...a, client: e.target.value.trim() })} />
      </Field>
      <Field
        label="Display ad unit"
        hint="The data-ad-slot of a responsive display ad unit (AdSense → Ads → By ad unit). Used wherever no specific unit is set below."
        error={unitOk(a.slot) ? null : 'Digits only'}
      >
        <input className="input" value={a.slot} placeholder="1234567890" inputMode="numeric" onChange={(e) => onChange({ ...a, slot: e.target.value.trim() })} />
      </Field>
      <details>
        <summary className="small" style={{ cursor: 'pointer', fontWeight: 600 }}>
          A separate ad unit per position (optional, for reporting)
        </summary>
        <div className="form-grid" style={{ marginTop: 10 }}>
          {(Object.keys(PLACEMENT_LABEL) as AdPlacement[]).map((k) => (
            <Field key={k} label={PLACEMENT_LABEL[k]} error={unitOk(a.slots[k]) ? null : 'Digits only'}>
              <input
                className="input"
                value={a.slots[k]}
                placeholder={a.slot || 'Ad unit'}
                inputMode="numeric"
                onChange={(e) => onChange({ ...a, slots: { ...a.slots, [k]: e.target.value.trim() } })}
              />
            </Field>
          ))}
        </div>
      </details>
      <Field
        label="Cookie consent"
        hint={
          a.consent === 'google'
            ? "Google's certified consent message asks visitors (required for personalised ads in the EEA, the UK and Switzerland). Create and publish it in AdSense → Privacy & messaging; the site's own banner is then hidden, and Cookie choices reopens Google's message."
            : "The site's own banner asks visitors. Ads wait until they choose and are non-personalised unless they accept. In the EEA, the UK and Switzerland Google only serves limited ads this way: use Google's message if you have visitors there."
        }
      >
        <Seg
          label="Cookie consent"
          value={a.consent}
          onChange={(consent) => onChange({ ...a, consent })}
          options={[
            ['google', "Google's consent message"],
            ['own', "The site's banner"],
          ]}
        />
      </Field>
      {a.client && clientOk && (
        <p className="small muted" style={{ margin: 0 }}>
          The site serves <code>/ads.txt</code> for this publisher ID and lets AdSense's crawler in through <code>/robots.txt</code>.
        </p>
      )}
    </div>
  );
}
